import { Cdr } from '@reliance/types/models/Cdr';
import { Inmate } from '@reliance/types/models/Inmate';
import { Row } from '@tanstack/react-table';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import Button from 'react-bootstrap/Button';
import { formatCurrency } from '../../format/Currency';
import { formatDuration } from '../../format/Duration';
import { formatPhoneNumber } from '../../format/PhoneNumber';
import { TaxDetailsRow } from '../../cdr/CdrTaxDetails';
import { useCdrsByInmateId } from '../../hooks/Cdr';
import { useTabLazyLoad } from '../../hooks/useTabLazyLoad';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import BaseHtmlTable from '../../table/BaseHtmlTable';
import { useServerTableFilterState } from '../../table/useServerTableFilterState';

type InmateCdrsProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
};

const COLUMNS = [
  { id: 'expand' },
  { id: 'id', meta: { sortName: 'id' } },
  { id: 'callTypes', meta: { sortName: 'category' } },
  { id: 'setupDate', meta: { sortName: 'setupDate' } },
  { id: 'endDate', meta: { sortName: 'endDate' } },
  { id: 'duration', meta: { sortName: 'durationSeconds' } },
  { id: 'direction', meta: { sortName: 'direction' } },
  { id: 'sourceIds', meta: { sortName: 'source' } },
  { id: 'destinations', meta: { sortName: 'destination' } },
  { id: 'completion', meta: { sortName: 'completion' } },
  { id: 'termination', meta: { sortName: 'terminationReason' } },
  { id: 'cost', meta: { sortName: 'cost' } },
  { id: 'billingCategory', meta: { sortName: 'billingCategory' } },
  { id: 'originatingPhoneNumber', meta: { sortName: 'originatingPhoneNumber' } },
];

const InmateCdrs: React.FC<InmateCdrsProps> = (props) => {
  const { inmate, tabSelected, refreshTrigger } = props;

  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  const baseRecord = useMemo(
    () => ({ inmateId: inmate.id, orderBy: '-setupDate' }) as Record<string, unknown>,
    [inmate.id]
  );
  const tableState = useServerTableFilterState(COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 500 },
    initialSorting: [{ id: 'setupDate', desc: true }],
  });

  const filters = useMemo(() => {
    const m = tableState.mergedRecord;
    const callTypes = m.callTypes as string | undefined;
    const sourceIds = m.sourceIds as string | undefined;
    const destinations = m.destinations as string | undefined;
    return {
      inmateId: inmate.id,
      page: m.page as number,
      perPage: m.perPage as number,
      orderBy: (m.orderBy as string | undefined) ?? '-setupDate',
      ...(callTypes != null && callTypes !== '' && { callTypes }),
      ...(sourceIds != null && sourceIds !== '' && { sourceIds }),
      ...(destinations != null && destinations !== '' && { destinations }),
    };
  }, [inmate.id, tableState.mergedRecord]);

  const cdrsResult = useCdrsByInmateId(null, filters);

  useTabLazyLoad({
    key: inmate.id,
    tabSelected,
    refreshTrigger,
    execute: useCallback(() => {
      if (inmate.id) {
        cdrsResult.execute(inmate.id);
      }
    }, [cdrsResult, inmate.id]),
  });

  useEffect(() => {
    if (tabSelected && inmate.id) {
      cdrsResult.execute(inmate.id);
    }
  }, [tabSelected, inmate.id, tableState.mergedRecord]);

  useNotifyTabLoading('cdrs', cdrsResult.loading);

  const cdrData = cdrsResult.result?.data ?? [];
  const total = cdrsResult.result?.total ?? 0;
  const perPage = cdrsResult.result?.perPage ?? tableState.pagination.pageSize;
  const totalPages = Math.ceil(total / perPage) || 1;
  const page = cdrsResult.result?.page ?? tableState.pagination.pageIndex + 1;

  const columnHelper = createColumnHelper<Cdr>();

  const formatDateTime = (val: Cdr['setupDate'] | Cdr['endDate']) => {
    if (!val) return '';
    const d = typeof val === 'object' && val && 'date' in val ? (val as { date: string }).date : String(val);
    return moment(d).format('YYYY/MM/DD HH:mm:ss');
  };

  const cdrColumns = useMemo(
    () => [
      columnHelper.display({
        id: 'expand',
        header: '',
        enableColumnFilter: false,
        cell: (info) => {
          const isExpanded = expandedRowId === info.row.original.id;
          return (
            <Button
              variant="link"
              size="sm"
              onClick={() => setExpandedRowId(isExpanded ? null : (info.row.original.id ?? null))}
              style={{ padding: 0, textDecoration: 'none', boxShadow: 'none', WebkitBoxShadow: 'none' }}>
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
            </Button>
          );
        },
        size: 30,
      }),
      columnHelper.accessor('id', {
        id: 'id',
        header: 'ID',
        meta: { sortName: 'id' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.category, {
        id: 'callTypes',
        header: 'Category',
        meta: { sortName: 'category' },
      }),
      columnHelper.accessor((row) => row.setupDate, {
        id: 'setupDate',
        header: 'Setup',
        cell: (info) => formatDateTime(info.getValue()),
        meta: { sortName: 'setupDate' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.endDate, {
        id: 'endDate',
        header: 'Ended',
        cell: (info) => formatDateTime(info.getValue()),
        meta: { sortName: 'endDate' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.durationSeconds, {
        id: 'duration',
        header: 'Duration',
        cell: (info) => formatDuration(info.getValue() ?? 0),
        meta: { sortName: 'durationSeconds' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.direction, {
        id: 'direction',
        header: 'Direction',
        meta: { sortName: 'direction' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.source, {
        id: 'sourceIds',
        header: 'Source',
        cell: (info) => {
          const row = info.row.original;
          return (
            <>
              {row.source}
              {row.sourceName && <span> ({row.sourceName})</span>}
            </>
          );
        },
        meta: { sortName: 'source' },
      }),
      columnHelper.accessor((row) => row.destination, {
        id: 'destinations',
        header: 'Destination',
        cell: (info) => {
          const row = info.row.original;
          const dest = row.destination ?? '';
          return (
            <>
              {dest ? formatPhoneNumber(dest) : ''}
              {row.destinationName && <span> ({row.destinationName})</span>}
            </>
          );
        },
        meta: { sortName: 'destination' },
      }),
      columnHelper.accessor((row) => row.completion, {
        id: 'completion',
        header: 'Completion',
        meta: { sortName: 'completion' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.terminationReason, {
        id: 'termination',
        header: 'Termination',
        meta: { sortName: 'terminationReason' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.cost, {
        id: 'cost',
        header: 'Cost',
        cell: (info) => formatCurrency(Number(info.getValue())),
        meta: { sortName: 'cost' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.billingCategory, {
        id: 'billingCategory',
        header: 'Billing',
        meta: { sortName: 'billingCategory' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.originatingPhoneNumber, {
        id: 'originatingPhoneNumber',
        header: 'Originating Phone Number',
        meta: { sortName: 'originatingPhoneNumber' },
        enableColumnFilter: false,
      }),
    ],
    [columnHelper, expandedRowId]
  );

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div>
        <BaseHtmlTable
          rows={cdrData}
          columnDefs={cdrColumns}
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          serverPagination={tableState.serverPagination}
          onServerPaginationChange={tableState.onServerPaginationChange}
          serverSorting={tableState.serverSorting}
          onServerSortingChange={tableState.onServerSortingChange}
          serverColumnFilters={tableState.serverColumnFilters}
          onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
          paginationMode="server"
          loading={cdrsResult.loading}
          pageSizeOptions={[100, 500, 1000]}
          isRowExpanded={(row: Row<Cdr>) => expandedRowId === row.original.id}
          getExpansionRow={(row: Row<Cdr>) => (
            <tr>
              <td colSpan={row.getVisibleCells().length} style={{ padding: 15, backgroundColor: '#f8f9fa' }}>
                <TaxDetailsRow cdr={row.original} />
              </td>
            </tr>
          )} />
      </div>
    </div>
  );
};

export default InmateCdrs;
